import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck, MapPin, Phone, CreditCard } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Transporter = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  // ── State ──────────────────────────────────────────────────────────────────
  const [transporters, setTransporters]                 = useState([]);
  const [filteredTransporters, setFilteredTransporters] = useState([]);
  const [loading, setLoading]                           = useState(true);
  const [searchTerm, setSearchTerm]                     = useState('');
  const [statusFilter, setStatusFilter]                 = useState('all');
  const [showModal, setShowModal]                       = useState(false);
  const [editMode, setEditMode]                         = useState(false);
  const [submitLoading, setSubmitLoading]               = useState(false);

  const emptyForm = {
    TransporterID:      null,
    Transporter:        '',
    ReferenceCode:      '',
    ContactPerson:      '',
    Address1:           '',
    Address2:           '',
    Address3:           '',
    Phone:              '',
    Fax:                '',
    Email:              '',
    WebSite:            '',
    MaxCreditDays:      '',
    MaxCreditLimit:     '',
    DiscountPercentage: '',
    StatusActive:       true,
    StatusConsignor:    false,
    Remarks:            '',
    ExporterCode:       ''
  };

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors]     = useState({});

  // ── Load ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchTransporters('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    if (companyId && yearId) fetchTransporters(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    filterTransporters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, transporters]);

  // ── API ────────────────────────────────────────────────────────────────────
  const fetchTransporters = async (status = 'all') => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Transporter.php', { params: { status } });
      if (res.data.success) {
        setTransporters(res.data.data || []);
      } else {
        alert('Failed to fetch transporters: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error fetching transporters:', err);
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching transporters.');
    } finally {
      setLoading(false);
    }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filterTransporters = () => {
    if (!searchTerm.trim()) { setFilteredTransporters(transporters); return; }
    const q = searchTerm.toLowerCase();
    setFilteredTransporters(
      transporters.filter(t =>
        t.Transporter?.toLowerCase().includes(q)   ||
        t.ReferenceCode?.toLowerCase().includes(q) ||
        t.ContactPerson?.toLowerCase().includes(q)
      )
    );
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!formData.Transporter.trim())
      errs.Transporter = 'Transporter name is required';
    if (formData.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email))
      errs.Email = 'Invalid email format';
    if (formData.MaxCreditDays !== '' && (isNaN(formData.MaxCreditDays) || Number(formData.MaxCreditDays) < 0))
      errs.MaxCreditDays = 'Must be a positive number';
    if (formData.MaxCreditLimit !== '' && (isNaN(formData.MaxCreditLimit) || Number(formData.MaxCreditLimit) < 0))
      errs.MaxCreditLimit = 'Must be a positive number';
    if (formData.DiscountPercentage !== '' && (isNaN(formData.DiscountPercentage) || Number(formData.DiscountPercentage) < 0 || Number(formData.DiscountPercentage) > 100))
      errs.DiscountPercentage = 'Must be 0 – 100';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        StatusActive:    formData.StatusActive    ? 1 : 0,
        StatusConsignor: formData.StatusConsignor ? 1 : 0,
      };

      const method = editMode ? 'put' : 'post';
      const res    = await apiClient[method]('/Transporter.php', payload);

      if (res.data.success) {
        alert(editMode ? 'Transporter updated successfully!' : 'Transporter created successfully!');
        setShowModal(false);
        resetForm();
        fetchTransporters(statusFilter);
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error saving transporter:', err);
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleEdit = async (transporter) => {
    try {
      const res = await apiClient.get(`/Transporter.php?id=${transporter.TransporterID}`);
      if (res.data.success) {
        const d = res.data.data;
        setFormData({
          TransporterID:      d.TransporterID,
          Transporter:        d.Transporter        || '',
          ReferenceCode:      d.ReferenceCode      || '',
          ContactPerson:      d.ContactPerson      || '',
          Address1:           d.Address1           || '',
          Address2:           d.Address2           || '',
          Address3:           d.Address3           || '',
          Phone:              d.Phone              || '',
          Fax:                d.Fax                || '',
          Email:              d.Email              || '',
          WebSite:            d.WebSite            || '',
          MaxCreditDays:      d.MaxCreditDays      ?? '',
          MaxCreditLimit:     d.MaxCreditLimit     ?? '',
          DiscountPercentage: d.DiscountPercentage ?? '',
          Remarks:            d.Remarks            || '',
          ExporterCode:       d.ExporterCode       || '',
          StatusActive:       Number(d.StatusActive)    === 1,
          StatusConsignor:    Number(d.StatusConsignor) === 1,
        });
        setEditMode(true);
        setShowModal(true);
        setErrors({});
      }
    } catch (err) {
      console.error('Error loading transporter:', err);
      alert('Error loading transporter details');
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transporter?')) return;
    try {
      const res = await apiClient.delete('/Transporter.php', { data: { TransporterID: id } });
      if (res.data.success) {
        alert('Transporter deleted successfully!');
        fetchTransporters(statusFilter);
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error deleting: ' + (err.response?.data?.message || 'Please try again.'));
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const set          = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });
  const setCheck     = (field) => (e) => setFormData({ ...formData, [field]: e.target.checked });
  const resetForm    = () => { setFormData(emptyForm); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const fmtNum = (v) => (v != null && v !== '') ? Number(v).toFixed(2) : '—';
  const fmtPct = (v) => (v != null && v !== '') ? `${Number(v).toFixed(2)}%` : '—';

  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="master-container">

      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2>Transporter Master</h2>
          <p className="subtitle">Manage transporters and haulage partners</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} /> Add Transporter
        </button>
      </div>

      {/* Toolbar */}
      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by name, code, or contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="consignor">Consignor</option>
          </select>
        </div>

        <div className="toolbar-info">
          Total: {filteredTransporters.length} {filteredTransporters.length === 1 ? 'transporter' : 'transporters'}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Loading transporters...</div>
      ) : filteredTransporters.length === 0 ? (
        <div className="empty-state">
          <p>No transporters found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Ref Code</th>
                <th>Transporter</th>
                <th>Contact Person</th>
                <th>Phone</th>
                <th>Email</th>
                <th className="text-right">Credit Limit</th>
                <th className="text-right">Discount %</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransporters.map((t) => (
                <tr key={t.TransporterID}>
                  <td className="font-mono">{t.ReferenceCode || '—'}</td>
                  <td className="font-semibold">{t.Transporter}</td>
                  <td>{t.ContactPerson || '—'}</td>
                  <td>
                    <div className="contact-item"><Phone size={14} />{t.Phone || '—'}</div>
                  </td>
                  <td>{t.Email || '—'}</td>
                  <td className="text-right" style={{ fontFamily: 'monospace' }}>{fmtNum(t.MaxCreditLimit)}</td>
                  <td className="text-right" style={{ fontFamily: 'monospace' }}>{fmtPct(t.DiscountPercentage)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {Number(t.StatusActive)    === 1 && <span className="status-badge active">Active</span>}
                      {Number(t.StatusActive)    === 0 && <span className="status-badge inactive">Inactive</span>}
                      {Number(t.StatusConsignor) === 1 && (
                        <span className="status-badge" style={{ background: '#ede9fe', color: '#5b21b6', border: '2px solid #7c3aed' }}>
                          Consignor
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="actions-column">
                    <button className="btn-icon btn-edit"   onClick={() => handleEdit(t)}                    title="Edit">   <Edit2  size={16} /></button>
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(t.TransporterID)}    title="Delete"> <Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Transporter' : 'Add New Transporter'}</h3>
              <button className="btn-close" onClick={closeModal}><X size={24} /></button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">

              {/* ── Basic Info ── */}
              <div className="form-section">
                <h4><Truck size={18} /> Basic Information</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="Transporter">Transporter Name <span className="required">*</span></label>
                    <input type="text" id="Transporter" autoFocus
                      value={formData.Transporter} onChange={set('Transporter')}
                      maxLength="100" placeholder="e.g., Fast Logistics LLC"
                      className={errors.Transporter ? 'error' : ''} />
                    {errors.Transporter && <span className="error-message">{errors.Transporter}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="ReferenceCode">Reference Code</label>
                    <input type="text" id="ReferenceCode"
                      value={formData.ReferenceCode}
                      onChange={(e) => setFormData({ ...formData, ReferenceCode: e.target.value.toUpperCase() })}
                      maxLength="50" placeholder="e.g., FAST01" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ContactPerson">Contact Person</label>
                    <input type="text" id="ContactPerson"
                      value={formData.ContactPerson} onChange={set('ContactPerson')}
                      maxLength="100" placeholder="e.g., John Smith" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ExporterCode">Exporter Code</label>
                    <input type="text" id="ExporterCode"
                      value={formData.ExporterCode}
                      onChange={(e) => setFormData({ ...formData, ExporterCode: e.target.value.toUpperCase() })}
                      maxLength="50" placeholder="e.g., EXP001" />
                  </div>
                </div>
              </div>

              {/* ── Address ── */}
              <div className="form-section">
                <h4><MapPin size={18} /> Address</h4>

                <div className="form-group">
                  <label htmlFor="Address1">Address Line 1</label>
                  <input type="text" id="Address1" value={formData.Address1} onChange={set('Address1')}
                    maxLength="100" placeholder="Street address" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="Address2">Address Line 2</label>
                    <input type="text" id="Address2" value={formData.Address2} onChange={set('Address2')}
                      maxLength="100" placeholder="Apartment, suite" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="Address3">Address Line 3</label>
                    <input type="text" id="Address3" value={formData.Address3} onChange={set('Address3')}
                      maxLength="100" placeholder="Additional info" />
                  </div>
                </div>
              </div>

              {/* ── Contact ── */}
              <div className="form-section">
                <h4><Phone size={18} /> Contact Information</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="Phone">Phone</label>
                    <input type="text" id="Phone" value={formData.Phone} onChange={set('Phone')}
                      maxLength="50" placeholder="+971-4-1234567" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="Fax">Fax</label>
                    <input type="text" id="Fax" value={formData.Fax} onChange={set('Fax')}
                      maxLength="50" placeholder="+971-4-7654321" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="Email">Email</label>
                    <input type="email" id="Email" value={formData.Email} onChange={set('Email')}
                      maxLength="50" placeholder="info@transporter.com"
                      className={errors.Email ? 'error' : ''} />
                    {errors.Email && <span className="error-message">{errors.Email}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="WebSite">Website</label>
                    <input type="text" id="WebSite" value={formData.WebSite} onChange={set('WebSite')}
                      maxLength="50" placeholder="www.transporter.com" />
                  </div>
                </div>
              </div>

              {/* ── Credit & Discount ── */}
              <div className="form-section">
                <h4><CreditCard size={18} /> Credit &amp; Discount</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="MaxCreditDays">Max Credit Days</label>
                    <input type="number" id="MaxCreditDays" min="0"
                      value={formData.MaxCreditDays} onChange={set('MaxCreditDays')}
                      placeholder="e.g., 30"
                      className={errors.MaxCreditDays ? 'error' : ''}
                      style={{ textAlign: 'right', fontFamily: 'monospace' }} />
                    {errors.MaxCreditDays && <span className="error-message">{errors.MaxCreditDays}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="MaxCreditLimit">Max Credit Limit</label>
                    <input type="number" id="MaxCreditLimit" min="0" step="0.01"
                      value={formData.MaxCreditLimit} onChange={set('MaxCreditLimit')}
                      placeholder="0.00"
                      className={errors.MaxCreditLimit ? 'error' : ''}
                      style={{ textAlign: 'right', fontFamily: 'monospace' }} />
                    {errors.MaxCreditLimit && <span className="error-message">{errors.MaxCreditLimit}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="DiscountPercentage">Discount %</label>
                    <input type="number" id="DiscountPercentage" min="0" max="100" step="0.01"
                      value={formData.DiscountPercentage} onChange={set('DiscountPercentage')}
                      placeholder="0.00"
                      className={errors.DiscountPercentage ? 'error' : ''}
                      style={{ textAlign: 'right', fontFamily: 'monospace' }} />
                    {errors.DiscountPercentage && <span className="error-message">{errors.DiscountPercentage}</span>}
                  </div>
                </div>
              </div>

              {/* ── Status & Remarks ── */}
              <div className="form-section">
                <h4>Status &amp; Remarks</h4>

                <div className="form-row" style={{ gap: '32px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {[
                    { key: 'StatusActive',    label: 'Active'    },
                    { key: 'StatusConsignor', label: 'Consignor' },
                  ].map(({ key, label }) => (
                    <label key={key}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                      <input
                        type="checkbox"
                        checked={!!formData[key]}
                        onChange={setCheck(key)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label htmlFor="Remarks">Remarks</label>
                  <textarea id="Remarks" rows="3"
                    value={formData.Remarks} onChange={set('Remarks')}
                    maxLength="200" placeholder="Additional notes..." />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={submitLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transporter;